/**
 * client/hologram-titles.ts
 * Responsabilidad: Motor de renderizado holográfico WebGL (PixiJS v8) para Títulos Honoríficos
 * y caché visual de catálogo de temporadas.
 *
 * Arquitectura:
 * - Proyección exclusiva en el HEMISFERIO INFERIOR (+Y) del jugador (Lower-Arc Cradle + Placa flotante)
 *   para garantizar 100% de visibilidad despejada de sombreros, coronas, caras, propulsores y alias/emotes superiores.
 * - Estilos procedurales: 'cyber_gold', 'crimson_plasma', 'astral_nebula', 'void_glitch', 'emerald_matrix'.
 * - Caché local precargada con los 24 títulos de la base de datos para renderizado inmediato en frame 0,
 *   con sincronización reactiva en segundo plano desde el servidor.
 */

import { Graphics, Text } from "pixi.js";

export type HologramStyle =
    | "cyber_gold"
    | "crimson_plasma"
    | "astral_nebula"
    | "void_glitch"
    | "emerald_matrix";

export interface HologramTitleDef {
    id: string;
    name: string;
    hologramStyle: HologramStyle;
    primaryColor: string;
    secondaryColor: string;
    icon: string;
}

/**
 * Diccionario de respaldo precargado con los 24 títulos sembrados en la base de datos.
 * Garantiza disponibilidad síncrona sin parpadeos desde el primer fotograma.
 */
export const FALLBACK_TITLES_MAP: Record<string, HologramTitleDef> = {
    // Ranking Eliminaciones (Kills)
    title_kills_1: {
        id: "title_kills_1",
        name: "EMPERADOR DE LA CARNICERÍA",
        hologramStyle: "cyber_gold",
        primaryColor: "#ef4444",
        secondaryColor: "#fbbf24",
        icon: "👑",
    },
    title_kills_2: {
        id: "title_kills_2",
        name: "SEÑOR DE LA GUERRA ALQUÍMICO",
        hologramStyle: "crimson_plasma",
        primaryColor: "#f87171",
        secondaryColor: "#fca5a5",
        icon: "⚔️",
    },
    title_kills_3: {
        id: "title_kills_3",
        name: "VERDUGO DEL ÉTER",
        hologramStyle: "crimson_plasma",
        primaryColor: "#dc2626",
        secondaryColor: "#fecaca",
        icon: "🗡️",
    },
    title_kills_4: {
        id: "title_kills_4",
        name: "CAZADOR IMPLACABLE",
        hologramStyle: "crimson_plasma",
        primaryColor: "#ea580c",
        secondaryColor: "#fdba74",
        icon: "🔥",
    },
    title_kills_5: {
        id: "title_kills_5",
        name: "DEPREDADOR ASTRAL",
        hologramStyle: "crimson_plasma",
        primaryColor: "#f97316",
        secondaryColor: "#fed7aa",
        icon: "⚡",
    },

    // Ranking Masa Máxima
    title_mass_1: {
        id: "title_mass_1",
        name: "COLOSO PRIMORDIAL",
        hologramStyle: "cyber_gold",
        primaryColor: "#a855f7",
        secondaryColor: "#fbbf24",
        icon: "🪐",
    },
    title_mass_2: {
        id: "title_mass_2",
        name: "TITÁN DEVORADOR DE MUNDOS",
        hologramStyle: "astral_nebula",
        primaryColor: "#c084fc",
        secondaryColor: "#38bdf8",
        icon: "🌌",
    },
    title_mass_3: {
        id: "title_mass_3",
        name: "LEVIATÁN DEL VACÍO",
        hologramStyle: "astral_nebula",
        primaryColor: "#9333ea",
        secondaryColor: "#e9d5ff",
        icon: "☄️",
    },
    title_mass_4: {
        id: "title_mass_4",
        name: "GIGANTE GRAVITACIONAL",
        hologramStyle: "astral_nebula",
        primaryColor: "#7e22ce",
        secondaryColor: "#d8b4fe",
        icon: "💎",
    },
    title_mass_5: {
        id: "title_mass_5",
        name: "MONOLITO ALQUÍMICO",
        hologramStyle: "astral_nebula",
        primaryColor: "#6366f1",
        secondaryColor: "#c7d2fe",
        icon: "🛡️",
    },

    // Ranking Mayor Racha (Streak)
    title_streak_1: {
        id: "title_streak_1",
        name: "DIOS DE LA RACHA INMORTAL",
        hologramStyle: "cyber_gold",
        primaryColor: "#f59e0b",
        secondaryColor: "#fef08a",
        icon: "🔥",
    },
    title_streak_2: {
        id: "title_streak_2",
        name: "FURIA INCONTENIBLE",
        hologramStyle: "void_glitch",
        primaryColor: "#ec4899",
        secondaryColor: "#f472b6",
        icon: "⚡",
    },
    title_streak_3: {
        id: "title_streak_3",
        name: "TORMENTA DE ANIQUILACIÓN",
        hologramStyle: "void_glitch",
        primaryColor: "#f43f5e",
        secondaryColor: "#fda4af",
        icon: "💥",
    },
    title_streak_4: {
        id: "title_streak_4",
        name: "EJECUTOR EN CADENA",
        hologramStyle: "void_glitch",
        primaryColor: "#eab308",
        secondaryColor: "#fde047",
        icon: "🎯",
    },
    title_streak_5: {
        id: "title_streak_5",
        name: "IMPULSO LETAL",
        hologramStyle: "void_glitch",
        primaryColor: "#d97706",
        secondaryColor: "#fcd34d",
        icon: "☄️",
    },

    // Ranking Tiempo de Supervivencia
    title_time_1: {
        id: "title_time_1",
        name: "CRONOMANTE SUPREMO",
        hologramStyle: "cyber_gold",
        primaryColor: "#10b981",
        secondaryColor: "#fbbf24",
        icon: "⏳",
    },
    title_time_2: {
        id: "title_time_2",
        name: "GUARDIÁN ETERNO",
        hologramStyle: "emerald_matrix",
        primaryColor: "#34d399",
        secondaryColor: "#a7f3d0",
        icon: "🛡️",
    },
    title_time_3: {
        id: "title_time_3",
        name: "SUPERVIVIENTE INMORTAL",
        hologramStyle: "emerald_matrix",
        primaryColor: "#059669",
        secondaryColor: "#6ee7b7",
        icon: "🌿",
    },
    title_time_4: {
        id: "title_time_4",
        name: "CENTINELA DEL TIEMPO",
        hologramStyle: "emerald_matrix",
        primaryColor: "#14b8a6",
        secondaryColor: "#99f6e4",
        icon: "🧭",
    },
    title_time_5: {
        id: "title_time_5",
        name: "VIAJERO INCANSABLE",
        hologramStyle: "emerald_matrix",
        primaryColor: "#06b6d4",
        secondaryColor: "#a5f3fc",
        icon: "✨",
    },

    // Títulos Especiales Adictivos
    title_special_kingslayer: {
        id: "title_special_kingslayer",
        name: "MATARREYES",
        hologramStyle: "crimson_plasma",
        primaryColor: "#f43f5e",
        secondaryColor: "#fbbf24",
        icon: "🗡️",
    },
    title_special_reaper: {
        id: "title_special_reaper",
        name: "PARCA INMORTAL",
        hologramStyle: "void_glitch",
        primaryColor: "#ef4444",
        secondaryColor: "#f43f5e",
        icon: "🩸",
    },
    title_special_hypernova: {
        id: "title_special_hypernova",
        name: "HIPERNOVA VIVIENTE",
        hologramStyle: "astral_nebula",
        primaryColor: "#38bdf8",
        secondaryColor: "#c084fc",
        icon: "🌌",
    },
    title_special_life: {
        id: "title_special_life",
        name: "SEÑOR DE LA VIDA",
        hologramStyle: "emerald_matrix",
        primaryColor: "#10b981",
        secondaryColor: "#a7f3d0",
        icon: "🌿",
    },
};

/** Mapa mutable dinámico del cliente enriquecido por el backend */
export const TITLES_MAP: Record<string, HologramTitleDef> = { ...FALLBACK_TITLES_MAP };

let isSyncing = false;

/**
 * Sincroniza en background el catálogo completo de títulos desde el backend
 * para reflejar cambios en la base de datos sin necesidad de rebuilds.
 */
export async function syncTitlesCatalogFromServer(providedCatalog?: any[]): Promise<void> {
    if (Array.isArray(providedCatalog)) {
        for (const item of providedCatalog) {
            if (item && typeof item.id === "string") {
                TITLES_MAP[item.id] = {
                    id: item.id,
                    name: item.name || item.id,
                    hologramStyle: (item.hologramStyle as HologramStyle) || "cyber_gold",
                    primaryColor: item.primaryColor || "#fbbf24",
                    secondaryColor: item.secondaryColor || "#fef08a",
                    icon: item.icon || "👑",
                };
            }
        }
        return;
    }

    if (isSyncing) return;
    isSyncing = true;
    try {
        const res = await fetch("/api/season/overview");
        if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data.titlesCatalog)) {
                for (const item of data.titlesCatalog) {
                    if (item && typeof item.id === "string") {
                        TITLES_MAP[item.id] = {
                            id: item.id,
                            name: item.name || item.id,
                            hologramStyle: (item.hologramStyle as HologramStyle) || "cyber_gold",
                            primaryColor: item.primaryColor || "#fbbf24",
                            secondaryColor: item.secondaryColor || "#fef08a",
                            icon: item.icon || "👑",
                        };
                    }
                }
            }
        }
    } catch (err) {
        console.warn("[HologramTitles] Error sincronizando catálogo de títulos:", err);
    } finally {
        isSyncing = false;
    }
}

if (typeof window !== "undefined") {
    window.addEventListener("alchem:title-equipped", () => {
        void syncTitlesCatalogFromServer();
    });
}

/**
 * Convierte un código hexadecimal '#ffffff' a número entero con fallback seguro.
 */
export function hexToNumber(hex: string, fallback: number = 0xfbbf24): number {
    if (!hex || typeof hex !== "string") return fallback;
    const clean = hex.startsWith("#") ? hex.slice(1) : hex;
    const parsed = parseInt(clean, 16);
    return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Dibuja autoritativamente el halo holográfico y la placa flotante en el hemisferio inferior del jugador.
 * Cero interferencia con sombreros, caras, coronas y alias/emotes superiores.
 */
export function drawPlayerTitleHologram(
    g: Graphics,
    titleText: Text,
    titleId: string,
    radius: number,
    playerZoom: number,
    nowMs: number
): void {
    if (!titleId || radius <= 12) {
        titleText.visible = false;
        return;
    }

    const def = TITLES_MAP[titleId];
    if (!def) {
        titleText.visible = false;
        return;
    }

    const primaryHex = hexToNumber(def.primaryColor, 0xfbbf24);
    const secondaryHex = hexToNumber(def.secondaryColor, 0xfef08a);

    // =========================================================================
    // 1. GEOMETRÍA DEL HALO HOLOGRÁFICO INFERIOR (LOWER-ARC CRADLE)
    // Se proyecta exclusivamente entre 0.20π y 0.80π (Polo Sur +Y = 0.50π).
    // Deja 100% despejado el hemisferio superior y el centro de la esfera.
    // =========================================================================
    const arcRadiusOuter = radius + 7 * playerZoom;
    const arcRadiusInner = radius + 3.5 * playerZoom;
    const pulse = 0.75 + 0.25 * Math.sin(nowMs * 0.006);

    // Riel curvo exterior principal
    g.arc(0, 0, arcRadiusOuter, 0.22 * Math.PI, 0.78 * Math.PI).stroke({
        color: primaryHex,
        width: Math.max(1.4, 2.2 * playerZoom),
        alpha: 0.9 * pulse,
    });

    // Riel curvo interior secundario
    g.arc(0, 0, arcRadiusInner, 0.28 * Math.PI, 0.72 * Math.PI).stroke({
        color: secondaryHex,
        width: Math.max(1.0, 1.5 * playerZoom),
        alpha: 0.85,
    });

    // =========================================================================
    // 2. DETALLES PROCEDIMENTALES POR ESTILO HOLOGRÁFICO
    // =========================================================================
    switch (def.hologramStyle) {
        case "cyber_gold": {
            // 5 diamantes imperiales dorados orbitales a lo largo del arco inferior (0.26π a 0.74π)
            const count = 5;
            for (let i = 0; i < count; i++) {
                const ang = 0.26 * Math.PI + (i / (count - 1)) * (0.48 * Math.PI);
                const dPulse = 1 + 0.15 * Math.sin(nowMs * 0.008 + i * 1.2);
                const dx = Math.cos(ang) * (arcRadiusOuter + 2 * playerZoom);
                const dy = Math.sin(ang) * (arcRadiusOuter + 2 * playerZoom);
                const dSize = Math.max(2, 3.8 * playerZoom * dPulse);

                const diamondPts = [
                    dx, dy - dSize,
                    dx + dSize * 0.65, dy,
                    dx, dy + dSize,
                    dx - dSize * 0.65, dy,
                ];
                g.poly(diamondPts, true).fill({ color: secondaryHex, alpha: 0.95 });
                g.poly(diamondPts, true).stroke({ color: 0xffffff, width: 1, alpha: 0.9 });
            }

            // Doble filigrana de luz solar en los extremos del arco
            for (const ang of [0.22 * Math.PI, 0.78 * Math.PI]) {
                const fx1 = Math.cos(ang) * (arcRadiusInner - 2 * playerZoom);
                const fy1 = Math.sin(ang) * (arcRadiusInner - 2 * playerZoom);
                const fx2 = Math.cos(ang) * (arcRadiusOuter + 5 * playerZoom);
                const fy2 = Math.sin(ang) * (arcRadiusOuter + 5 * playerZoom);
                g.moveTo(fx1, fy1).lineTo(fx2, fy2).stroke({ color: 0xffffff, width: 1.5 * playerZoom, alpha: 0.9 });
            }
            break;
        }

        case "crimson_plasma": {
            // 6 picos de plasma carmesí proyectados hacia afuera a lo largo del arco inferior
            const spikes = 6;
            for (let i = 0; i < spikes; i++) {
                const ang = 0.25 * Math.PI + (i / (spikes - 1)) * (0.50 * Math.PI);
                const spikeLen = (4 + 3 * Math.sin(nowMs * 0.015 + i * 2)) * playerZoom;
                const bx = Math.cos(ang) * arcRadiusOuter;
                const by = Math.sin(ang) * arcRadiusOuter;
                const tx = Math.cos(ang) * (arcRadiusOuter + spikeLen);
                const ty = Math.sin(ang) * (arcRadiusOuter + spikeLen);
                g.moveTo(bx, by).lineTo(tx, ty).stroke({
                    color: primaryHex,
                    width: Math.max(1.5, 2.5 * playerZoom),
                    alpha: 0.92,
                });
            }

            // Motas ígneas ascendentes desde el arco inferior
            for (let m = 0; m < 4; m++) {
                const mSeed = ((nowMs * 0.004 + m * 0.25) % 1);
                const mAng = 0.30 * Math.PI + m * 0.13 * Math.PI;
                const mDist = arcRadiusOuter + (1 - mSeed) * 8 * playerZoom;
                const mx = Math.cos(mAng) * mDist;
                const my = Math.sin(mAng) * mDist - mSeed * 10 * playerZoom;
                const mSize = Math.max(1, (2 - mSeed) * playerZoom);
                g.circle(mx, my, mSize).fill({ color: secondaryHex, alpha: (1 - mSeed) * 0.85 });
            }
            break;
        }

        case "astral_nebula": {
            // Halo difuso cósmico de 3 arcos concéntricos inferiores
            for (let k = 1; k <= 3; k++) {
                const rArc = arcRadiusOuter + k * 2.8 * playerZoom;
                const aArc = (0.45 / k) * pulse;
                g.arc(0, 0, rArc, 0.24 * Math.PI, 0.76 * Math.PI).stroke({
                    color: primaryHex,
                    width: 1.2 * playerZoom,
                    alpha: aArc,
                });
            }

            // 6 microestrellas centelleantes orbitando el hemisferio sur
            const starCount = 6;
            for (let s = 0; s < starCount; s++) {
                const sAng = 0.26 * Math.PI + (s / (starCount - 1)) * (0.48 * Math.PI) + Math.sin(nowMs * 0.003 + s) * 0.04;
                const sDist = arcRadiusOuter + 4.5 * playerZoom;
                const sx = Math.cos(sAng) * sDist;
                const sy = Math.sin(sAng) * sDist;
                const sTwinkle = 0.5 + 0.5 * Math.sin(nowMs * 0.009 + s * 1.5);
                const sRadius = Math.max(1.2, 2.2 * playerZoom * sTwinkle);

                g.circle(sx, sy, sRadius).fill({ color: 0xffffff, alpha: 0.95 });
                g.circle(sx, sy, sRadius * 1.6).fill({ color: secondaryHex, alpha: 0.4 * sTwinkle });
            }
            break;
        }

        case "void_glitch": {
            // Segmentos cibernéticos discontinuos con efecto de escaneo rápido en magenta/oro
            const segCount = 7;
            for (let i = 0; i < segCount; i++) {
                const glitchShift = Math.sin(nowMs * 0.02 + i * 1.7);
                if (glitchShift < -0.3) continue; // Efecto de parpadeo estroboscópico de glitch
                const aStart = 0.23 * Math.PI + (i / segCount) * (0.54 * Math.PI);
                const aEnd = aStart + (0.05 * Math.PI);
                const col = i % 2 === 0 ? primaryHex : secondaryHex;
                g.arc(0, 0, arcRadiusOuter + (glitchShift > 0.6 ? 2 * playerZoom : 0), aStart, aEnd).stroke({
                    color: col,
                    width: Math.max(1.6, 2.8 * playerZoom),
                    alpha: 0.95,
                });
            }
            break;
        }

        case "emerald_matrix": {
            // Líneas de proyección vertical tipo matriz cuántica que bajan desde el arco hacia la placa
            const lineCount = 5;
            const yPlateTop = radius + 10 * playerZoom;
            for (let l = 0; l < lineCount; l++) {
                const ang = 0.28 * Math.PI + (l / (lineCount - 1)) * (0.44 * Math.PI);
                const lx = Math.cos(ang) * arcRadiusOuter;
                const ly = Math.sin(ang) * arcRadiusOuter;
                const pAlpha = 0.4 + 0.4 * Math.sin(nowMs * 0.01 + l * 1.5);
                g.moveTo(lx, ly).lineTo(lx * 0.85, yPlateTop).stroke({
                    color: primaryHex,
                    width: Math.max(1, 1.2 * playerZoom),
                    alpha: pAlpha,
                });
            }
            break;
        }
    }

    // =========================================================================
    // 3. PLACA HOLOGRÁFICA FLOTANTE INFERIOR (Cápsula Sci-Fi con Scanline Láser)
    // yPlate = radius + 15 * playerZoom
    // =========================================================================
    const yPlate = radius + 15 * playerZoom;
    const cleanName = (def.name || "").replace(/^[\p{Extended_Pictographic}\uFE0F\s]+/u, "").trim();
    const icon = def.icon ? def.icon.trim() : "";
    const label = icon ? `⟪ ${icon} ${cleanName} ${icon} ⟫` : `⟪ ${cleanName} ⟫`;

    if (titleText.text !== label) {
        titleText.text = label;
    }

    const textScale = Math.max(0.70, Math.min(1.25, playerZoom * 0.92));
    titleText.position.set(0, yPlate);
    titleText.scale.set(textScale);
    if (titleText.style.fill !== secondaryHex) {
        titleText.style.fill = secondaryHex;
    }
    if (!titleText.style.stroke || (titleText.style.stroke as any).color !== 0x050811) {
        titleText.style.stroke = { color: 0x050811, width: 3.5 };
    }
    titleText.visible = true;

    // Dimensiones dinámicas seguras para la cápsula de fondo
    const plateW = Math.max(70 * textScale, titleText.width + 16 * textScale);
    const plateH = Math.max(15 * textScale, titleText.height + 5 * textScale);
    const cornerRadius = 6 * textScale;

    // Cápsula sci-fi translúcida detrás del texto
    g.roundRect(-plateW / 2, yPlate - plateH / 2, plateW, plateH, cornerRadius)
        .fill({ color: 0x090d16, alpha: 0.82 })
        .stroke({
            color: primaryHex,
            width: Math.max(1.2, 1.8 * playerZoom),
            alpha: 0.9 * pulse,
        });

    // Línea de escaneo láser horizontal (scanline) recorriendo la cápsula
    const scanOffset = (nowMs * 0.025) % plateH;
    const scanY = yPlate - plateH / 2 + scanOffset;
    g.moveTo(-plateW / 2 + 4 * textScale, scanY)
        .lineTo(plateW / 2 - 4 * textScale, scanY)
        .stroke({
            color: secondaryHex,
            width: 1.2,
            alpha: 0.85,
        });
}
